<script lang="ts" setup>
import { useElementBounding, useWindowSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import IconLeftPanelClose from '@/assets/icons/chevron_double_left.svg'
import IconLeftPanelOpen from '@/assets/icons/chevron_double_right.svg'
import { useAppStore } from '@/stores/app.store'
import SharedButton from './SharedButton.vue'

defineProps<{
  mobile?: boolean
  sticky?: boolean
}>()

const { asideCollapsed, isMobile } = storeToRefs(useAppStore())

const { toggleAsideCollapsed } = useAppStore()

const { t } = useI18n()

const buttonLabel = computed(() => (asideCollapsed.value ? t('app.aside.expand') : t('app.aside.collapse')))

const aside = ref<HTMLElement | undefined>()
const header = ref<HTMLElement | undefined>()

const { top, bottom } = useElementBounding(aside)
const { height: windowHeight } = useWindowSize()

const sectionHeight = computed(() =>
  !isMobile.value
    ? windowHeight.value
    - (header.value?.clientHeight ? header.value.clientHeight : 0)
    - (top.value > 0 ? top.value : 0)
    - (bottom.value < windowHeight.value ? windowHeight.value - bottom.value : 0)
    : undefined,
)
</script>

<template>
  <aside
    ref="aside"
    class="aside-collapsible"
    :class="{ collapsed: asideCollapsed, sticky }"
  >
    <article>
      <header ref="header">
        <div class="aside-collapsible-header-wrapper">
          <slot name="header" />
        </div>
        <SharedButton
          v-if="!isMobile"
          id="aside_button_toggle"
          :aria-label="buttonLabel"
          size="small"
          text
          :title="buttonLabel"
          @click="toggleAsideCollapsed()"
        >
          <component :is="asideCollapsed ? IconLeftPanelOpen : IconLeftPanelClose" />
        </SharedButton>
      </header>
      <section :style="{ height: `${sectionHeight}px` }">
        <slot />
      </section>
    </article>
  </aside>
</template>

<style lang="scss" scoped>
  .aside-collapsible section {
    :deep(.map-wrapper.rounded) {
      border: 1px solid var(--surface-border);
    }
  }

  @media screen and (max-width: 767px) {
    .aside-collapsible {
      container: aside-collapsible / inline-size;
      background-color: var(--surface-card);
      border: 1px solid var(--surface-border);
      border-radius: var(--border-radius);

      > article {
        > header {
          align-items: center;
          display: flex;
          gap: var(--inline-spacing);
          height: 4rem;
          padding-inline: 1rem;

          > .aside-collapsible-header-wrapper {
            align-items: center;
            display: grid;
            gap: var(--inline-spacing);
            grid-template-columns: 1fr;
            justify-content: space-between;
            min-width: 0;
            width: 100%;

            &:has(> button) {
              grid-template-columns: minmax(0, min-content) 2rem;
            }

            :deep(h4) {
              font-size: 1.125rem;
            }
          }

          :deep(button) {
            svg {
              color: var(--primary-color);
            }
          }
        }

        > section {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1rem;
          width: 100%;

          &:has(details) {
            padding: 0;
          }

          :deep(.label-and-value) {
            font-size: 90%;
            gap: 0;
          }
        }
      }
    }
  }

  @media screen and (min-width: 768px) {
    .aside-collapsible {
      background-color: var(--surface-card);
      border-right: 1px solid var(--surface-border);

      > article {
        > header {
          align-items: center;
          border-bottom: 1px solid var(--surface-border);
          display: grid;
          gap: var(--inline-spacing);
          grid-template-columns: auto 2rem;
          height: 4rem;
          left: 0;
          padding-inline: 1rem;
          position: absolute;
          right: 0;
          transition: left 0.3s ease-in-out;

          > .aside-collapsible-header-wrapper {
            align-items: center;
            display: grid;
            gap: var(--inline-spacing);
            grid-template-columns: 1fr;
            min-width: 0;

            &:has(> button) {
              grid-template-columns: minmax(0, min-content) 2rem;
            }
          }

          > .aside-collapsible-header-wrapper > :deep(h4) {
            opacity: 1;
            transition: opacity 0.3s ease-in-out;
          }

          :deep(button) {
            svg {
              color: var(--text-color-secondary);
            }

            &:active,
            &:focus,
            &:hover {
              svg {
                color: var(--primary-color);
              }
            }
          }
        }

        > section {
          display: flex;
          flex-direction: column;
          gap: 0;
          left: 0;
          overflow-y: auto;
          padding: 1rem;
          position: absolute;
          right: 0;
          top: 4rem;
          transition:
            left 0.3s ease-in-out,
            opacity 0.2s ease-in-out,
            visibility 0.2s ease-in-out;
          visibility: visible;
          width: 100%;

          &:has(details) {
            padding: 0;
          }

          :deep(.label-and-value) {
            font-size: 90%;
            gap: 0;
          }
        }
      }

      &.collapsed {
        > article {
          > header {
            left: -8rem;

            > .aside-collapsible-header-wrapper > :deep(h4) {
              opacity: 0;
            }

            > .aside-collapsible-header-wrapper:has(> button) {
              > :deep(button) {
                position: absolute !important;
                right: calc(1rem + 2px);
                top: 5rem;
              }
            }
          }

          > section {
            left: -8rem;
            opacity: 0;
            overflow-y: hidden;
            visibility: hidden;
          }
        }
      }
    }

    .aside-collapsible.sticky {
      position: relative;

      > article {
        position: sticky;
        top: 0;
      }
    }

    .aside-collapsible :deep(> .menu-sticky) {
      position: sticky;
      top: var(--top-sticky);
    }
  }
</style>
